import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

// Masked line reveal — each line clips up from below
const lineContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1.05, ease: [0.16, 1, 0.3, 1] } },
};

function MaskLine({ children, className = "" }) {
  return (
    <span className="block overflow-hidden">
      <motion.span variants={lineChild} className={`block ${className}`}>
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  // Parallax: background drifts slower, content lifts + fades
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#08080A]"
    >
      {/* Parallax background */}
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src="https://images.unsplash.com/photo-1652820330042-92a30b18abc5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHxrYXJuYXRha2ElMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzgzNDMzNTY2fDA&ixlib=rb-4.1.0&q=85"
          alt="Karnataka landscape"
          className="w-full h-full object-cover"
        />
        <motion.div
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0 bg-gradient-to-r from-[#08080A] via-[#08080A]/70 to-[#08080A]/20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08080A]/60 via-transparent to-[#08080A]" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-40 md:pt-56 pb-24 min-h-screen flex flex-col justify-between"
      >
        <div className="max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="overline mb-8 flex items-center gap-3"
            data-testid="hero-overline"
          >
            <span className="h-px w-10 bg-[#D4AF37]" />
            RUDRA EMPIRE · Welcome to the World of Business
          </motion.div>

          <motion.h1
            variants={lineContainer}
            initial="hidden"
            animate="show"
            data-testid="hero-headline"
            className="font-display text-6xl sm:text-7xl lg:text-[7rem] leading-[0.95] text-neutral-50"
          >
            <MaskLine>RUDRA EMPIRE</MaskLine>
            <MaskLine className="italic text-[#D4AF37] text-4xl sm:text-5xl lg:text-6xl mt-4">A Private Business House Since 1995.</MaskLine>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            data-testid="hero-subheading"
            className="mt-10 text-base md:text-lg text-neutral-400 max-w-2xl leading-relaxed tracking-wide"
          >
            Strategic land. Local intelligence. Institutional execution. RUDRA EMPIRE identifies fragmented privately held land in Karnataka, engages directly with landowners, undertakes structured due diligence, and consolidates parcels around defined institutional requirements — for Government bodies, public-sector programmes and corporate counterparties.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5"
          >
            <a
              data-testid="hero-cta-primary"
              href="#contact"
              className="group inline-flex items-center gap-3 bg-[#D4AF37] text-[#08080A] px-8 py-4 text-xs tracking-[0.24em] uppercase font-semibold hover:bg-[#F0D88B] transition-colors duration-300"
            >
              Initiate an Enquiry
              <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
            </a>
            <a
              data-testid="hero-cta-secondary"
              href="#about"
              className="link-gold inline-flex items-center gap-3 text-xs tracking-[0.24em] uppercase text-neutral-200 px-2 py-4"
            >
              Our story
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="hidden md:flex items-end justify-between text-neutral-500"
        >
          <div className="flex items-center gap-3 text-xs tracking-[0.22em] uppercase">
            <motion.span
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            >
              <ArrowDown strokeWidth={1} className="w-4 h-4" />
            </motion.span>
            Scroll to explore
          </div>
          <div className="text-right font-display italic text-neutral-400 text-lg max-w-xs leading-snug">
            “Land is not a commodity; it is a covenant between generations.”
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
